import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {
    SupernumeraryTooth,
    ToothHistoryDataSource,
    ToothManualAnnotation,
    ToothManualAnnotationMap,
    ToothMarkerTooltipEntry,
    ToothMarkerTooltipMap,
    ToothTreatPositionLayers,
    toothTreatPosDto,
    toothTreated
} from '../models/tooth-treat-position';
import { EasyNotificationService } from 'src/app/services/easy-notification.service';
import { UserCache } from 'src/app/services/user-cache';
import { RuleSeverity } from '../services/dental-treat-rule-from-eprohis.service';

type FaceManualTag = 'caries' | 'cantOD' | 'cantSea';
type ToothManualTag =
    'missing' |
    'extracted' |
    'impactionTooth' |
    'residualRoot' |
    'periodontitis' |
    'abscess' |
    'fracture' |
    'crown' |
    'pontic' |
    'implant' |
    'post' |
    'veneer' |
    'inlay';
type ToothMapLayer = 'manual' | ToothHistoryDataSource;
type SurfaceDentition = 'permanent' | 'primary';
type ContinuousAnnotationSelection =
    { kind: 'face'; tag: FaceManualTag } |
    { kind: 'tooth'; tag: ToothManualTag };

interface AnnotationMenuTarget {
    kind: 'face' | 'tooth' | 'continuous';
    toothPos: string;
    surfaceToothPos?: string;
    face?: string;
    x: number;
    y: number;
}

interface SupernumeraryDragState {
    id: string;
    pointerId: number;
    startClientX: number;
    startClientY: number;
    face?: string;
    isNew: boolean;
    original?: SupernumeraryTooth;
}

interface SupernumeraryTemplate {
    root: HTMLElement;
    face?: HTMLElement;
    width: number;
}

interface SupernumerarySlot {
    x: number;
    width: number;
    scale: number;
}

interface ToothAnatomyGeometry {
    cervicalX: number;
    cervicalY: number;
    overlayAxisX: number;
    overlayCervicalY: number;
    crownMidpointY: number;
    rootApexX: number;
    rootApexY: number;
    longAxisStartY: number;
    longAxisEndY: number;
    viewBox: string;
    overlayWidth: number;
    overlayHeight: number;
    svgScaleX: number;
    svgScaleY: number;
}

@Component({
    selector: 'app-tooth-pos-map',
    templateUrl: './tooth-positions-map.component.html',
    styleUrls: ['./tooth-positions-map.css']
})
export class ToothPositionsMapComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    colorRed = '#FF0000';
    private viewInitialized = false;
    private mapResizeObserver: any = null;
    baseMapWidth = 0;
    baseMapHeight = 0;
    mapScale = 1;

    @ViewChild('mapViewport') private mapViewport: ElementRef<HTMLElement>;
    @ViewChild('toothLayout') private toothLayout: ElementRef<HTMLElement>;
    @ViewChild('supernumerarySource') private supernumerarySource: ElementRef<HTMLElement>;
    @ViewChild('supernumeraryLayer') private supernumeraryLayer: ElementRef<HTMLElement>;

    @Input() isHelperPadOpened?: string = '';
    @Input() toothTreatPos: toothTreatPosDto[] = [];
    @Input() toothTreatPosLayers: ToothTreatPositionLayers = { internal: [], ic: [], cloud: [] };
    @Input() toothMarkerTooltips: ToothMarkerTooltipMap = {};
    @Input() manualAnnotations: ToothManualAnnotationMap = {};
    @Input() manualMarkerDate: string = '';
    @Input() supernumeraryTeeth: SupernumeraryTooth[] = [];
    @Input() manualAnnotationDisabled: boolean = false;
    @Input() hideLegend: boolean = false;
    @Input() test: boolean = false;
    @Output() toothPosClick = new EventEmitter<toothTreatPosDto>();
    @Output() manualAnnotationsChange = new EventEmitter<ToothManualAnnotationMap>();
    @Output() supernumeraryTeethChange = new EventEmitter<SupernumeraryTooth[]>();

    annotationMenu: AnnotationMenuTarget | null = null;
    continuousAnnotation: ContinuousAnnotationSelection | null = null;
    continuousSurfaceDentition: SurfaceDentition = 'permanent';
    markerTooltipVisible = false;
    markerTooltipShowToothPositions = false;
    markerTooltipX = 0;
    markerTooltipY = 0;
    markerTooltipEntries: ToothMarkerTooltipEntry[] = [];
    private markerTooltipKey = '';
    private supernumeraryDrag: SupernumeraryDragState | null = null;
    private supernumerarySequence = 0;
    private readonly supernumeraryTemplates = new Map<string, SupernumeraryTemplate>();
    visibleLayers: Record<ToothMapLayer, boolean> = {
        manual: true,
        internal: true,
        ic: true,
        cloud: true
    };

    private readonly severityOrder: Record<RuleSeverity, number> = {
        extracted: 0,
        forbidden: 1,
        warning: 2,
        info: 3
    };
    private readonly severityColors: Record<RuleSeverity, string> = {
        extracted: '#3f3f3f',
        forbidden: '#c0392b',
        warning: '#e67e22',
        info: '#1565c0'
    };
    private readonly toothArchRows: string[][] = [
        ['18', '17', '16', '15', '14', '13', '12', '11', '21', '22', '23', '24', '25', '26', '27', '28'],
        ['48', '47', '46', '45', '44', '43', '42', '41', '31', '32', '33', '34', '35', '36', '37', '38'],
        ['55', '54', '53', '52', '51', '61', '62', '63', '64', '65'],
        ['85', '84', '83', '82', '81', '71', '72', '73', '74', '75']
    ];
    private readonly toothQuadrants: string[][] = [
        ['18', '17', '16', '15', '14', '13', '12', '11'],
        ['21', '22', '23', '24', '25', '26', '27', '28'],
        ['48', '47', '46', '45', '44', '43', '42', '41'],
        ['31', '32', '33', '34', '35', '36', '37', '38'],
        ['55', '54', '53', '52', '51'],
        ['61', '62', '63', '64', '65'],
        ['85', '84', '83', '82', '81'],
        ['71', '72', '73', '74', '75']
    ];
    private readonly crossMidlinePairs: Partial<Record<string, [string, string]>> = {
        '11': ['11', '21'],
        '21': ['11', '21'],
        '31': ['31', '41'],
        '41': ['31', '41'],
        '51': ['51', '61'],
        '61': ['51', '61'],
        '71': ['71', '81'],
        '81': ['71', '81']
    };

    constructor(private notification: EasyNotificationService)
    {
    }

    ngOnInit() {
        /** 測試 */
        // this.setToothTreatPost();
        // if (this.test) this.setToothTreatPost();
        // var cln = UserCache.getLoginUser().Clinic;
        // if (cln.Id == 10014) {
        //     this.test = true;
        // }
        /** end 測試 */
        // console.log('toothTreatPos >>>>>', this.toothTreatPos);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.manualAnnotationDisabled?.currentValue) {
            this.annotationMenu = null;
            this.continuousAnnotation = null;
            this.cancelSupernumeraryDrag();
        }
        if ((changes.toothTreatPos || changes.toothTreatPosLayers || changes.toothMarkerTooltips ||
            changes.manualAnnotations || changes.manualMarkerDate || changes.supernumeraryTeeth) && this.viewInitialized) {
            this.setDefaultToothPos();
        }
    }

    ngAfterViewInit() {
        this.captureSupernumeraryTemplates();
        this.viewInitialized = true;
        this.setDefaultToothPos();
        this.initializeResponsiveMap();
    }

    ngOnDestroy() {
        this.hideMarkerTooltip();
        if (this.mapResizeObserver) {
            this.mapResizeObserver.disconnect();
            this.mapResizeObserver = null;
        }
        window.removeEventListener('resize', this.updateMapScale);
    }

    // def-value for test
    setToothTreatPost() {
        this.toothTreatPos = [];
        for (var i = 1; i <= 8; i++) {
            if (i <= 4) {  // 恆齒
                for (var j = 1; j <= 8; j++) {
                    var toothPos: toothTreatPosDto = new toothTreatPosDto();
                    var treatedRcd: toothTreated = new toothTreated();
                    toothPos.posNo = '' + i + j;

                    if (j == 7) { treatedRcd.canENDO = false; } else { treatedRcd.canENDO = true; }
                    if (j == 4 || j == 8) { treatedRcd.canExt = false; } else { treatedRcd.canExt = true; }
                    if (j == 6) { treatedRcd.canSea = false; } else { treatedRcd.canSea = true; }
                    if (j == 3) {
                        treatedRcd.cantOD = ['c', 'u', 'r'];
                    } else {
                        treatedRcd.cantOD = [];
                    }

                    toothPos.treatRecord = treatedRcd;
                    this.toothTreatPos.push(toothPos);
                }
            } else {    // 乳齒
                for (var j = 1; j <= 5; j++) {
                    var toothPos: toothTreatPosDto = new toothTreatPosDto();
                    var treatedRcd: toothTreated = new toothTreated();
                    toothPos.posNo = '' + i + j;

                    if (j == 3) { treatedRcd.canENDO = false; } else { treatedRcd.canENDO = true; }
                    if (j == 2) { treatedRcd.canExt = false; } else { treatedRcd.canExt = true; }
                    if (j == 4) { treatedRcd.canSea = false; } else { treatedRcd.canSea = true; }
                    if (j == 5) {
                        treatedRcd.cantOD = ['c', 'r', 'l', 'b'];
                    } else {
                        treatedRcd.cantOD = [];
                    }

                    toothPos.treatRecord = treatedRcd;
                    this.toothTreatPos.push(toothPos);
                }
            }
        }
    }

    /** 初始化牙歷圖 */
    setDefaultToothPos() {
        this.resetToothHist();
        // 設定恆齒
        this.setNormalTeethHist();
        // 設定乳齒牙面
        this.setBabyTeethHist();
        this.renderSupernumeraryTeeth();
        this.renderProstheticConnections();
    }

    private resetToothHist() {
        const layout = this.getToothLayoutElement();
        this.hideMarkerTooltip();
        if (this.supernumeraryLayer?.nativeElement) this.supernumeraryLayer.nativeElement.innerHTML = '';
        this.resetSupernumeraryQuadrantLayout();
        layout?.querySelectorAll<HTMLElement>('[data-tooth-marker-key]').forEach(element =>
            element.removeAttribute('data-tooth-marker-key'));
        layout?.querySelectorAll('.tooth-prosthetic-connector').forEach(connector => connector.remove());
        layout?.querySelectorAll<HTMLElement>('.tooth').forEach(tooth => {
            tooth.removeAttribute('data-manual-label');
            tooth.removeAttribute('title');
            tooth.classList.remove(
                'manual-missing',
                'manual-extracted',
                'manual-impaction',
                'manual-residual-root',
                'manual-periodontitis',
                'manual-abscess',
                'manual-fracture',
                'manual-crown',
                'manual-pontic',
                'manual-implant',
                'manual-post',
                'manual-veneer',
                'manual-inlay'
            );
            tooth.style.removeProperty('--impaction-angle');
            tooth.style.removeProperty('--cervical-x');
            tooth.style.removeProperty('--cervical-y');
            tooth.querySelectorAll('.tooth-anatomy-overlay').forEach(overlay => overlay.remove());
            tooth.querySelectorAll('.tooth-anatomy-container').forEach(container =>
                container.classList.remove('tooth-anatomy-container'));
        });

        for (var i = 1; i <= 4; i++) {
            for (var j = 1; j <= 8; j++) {
                var pos = i.toString() + j.toString();
                ['c', 'r', 'l', 'u', 'b'].forEach(face => {
                    this.setElementFill(pos + face, '#FFFFFF');
                    this.setElementAttribute(pos + face, 'fill-opacity', '1');
                    this.removeFaceOutlineAttribute(pos + face, 'data-marker-severity');
                    this.removeFaceOutlineAttribute(pos + face, 'data-manual-marked');
                });
                this.setElementAttribute(pos + 's', 'fill-opacity', '0');
                this.setElementAttribute(pos + 's', 'stroke-opacity', '0');
                this.removeFaceOutlineAttribute(pos + 's', 'data-marker-severity');
                this.setElementAttribute('ext-' + pos, 'fill-opacity', '0');
                this.setElementAttribute('endo' + pos, 'fill-opacity', '0');
            }
        }

        for (var i = 5; i <= 8; i++) {
            for (var j = 1; j <= 5; j++) {
                var pos = i.toString() + j.toString();
                this.setElementAttribute('ext-' + pos, 'fill-opacity', '0');
                this.setElementAttribute('endo' + pos, 'fill-opacity', '0');
            }
        }
    }

    /** 設定恆齒治療牙歷圖 */
    setNormalTeethHist() {
        for (var i = 1; i <= 4; i++) {
            for (var j = 1; j <= 8; j++) {
                var pos = (i.toString() + j.toString());
                var facePos = pos;
                this.setToothFace(facePos, false);
            }
        }
    }

    /** 設定乳齒治療牙歷圖 */
    setBabyTeethHist() {
        for (var i = 5; i <= 8; i++) {
            for (var j = 1; j <= 5; j++) {
                var pos = (i.toString() + j.toString());
                var facePos = pos;
                this.setToothFace(facePos, true);
            }
        }
    }

    toggleHistoryLayer(layer: ToothMapLayer) {
        this.visibleLayers[layer] = !this.visibleLayers[layer];
        this.setDefaultToothPos();
    }

    onMarkerPointerOver(event: PointerEvent) {
        this.updateMarkerTooltip(event);
    }

    onMarkerPointerMove(event: PointerEvent) {
        this.updateMarkerTooltip(event);
    }

    onMarkerPointerOut(event: PointerEvent) {
        const nextKey = this.getMarkerTooltipKey(event.relatedTarget);
        if (nextKey !== this.markerTooltipKey) this.hideMarkerTooltip();
    }

    getTooltipSourceLabel(source: ToothMarkerTooltipEntry['source']): string {
        return source === 'internal' ? '\u9662\u5167' : '\u4ED6\u9662';
    }

    getSeverityColor(severity: RuleSeverity): string {
        return this.severityColors[severity];
    }

    private updateMarkerTooltip(event: PointerEvent) {
        const key = this.getMarkerTooltipKey(event.target);
        if (!key) {
            this.hideMarkerTooltip();
            return;
        }

        const entries = this.getMarkerTooltipEntries(key);
        if (entries.length === 0) {
            this.hideMarkerTooltip();
            return;
        }

        this.markerTooltipKey = key;
        this.markerTooltipEntries = entries;
        this.markerTooltipShowToothPositions = new Set(entries
            .map(entry => entry.toothPos)
            .filter(toothPos => !!toothPos)).size > 1;
        this.markerTooltipVisible = true;
        this.markerTooltipX = Math.min(
            Math.max(8, event.clientX + 12),
            Math.max(8, window.innerWidth - 300)
        );
        this.markerTooltipY = Math.min(
            Math.max(8, event.clientY + 12),
            Math.max(8, window.innerHeight - Math.max(40, entries.length * 24 + 16))
        );
    }

    private hideMarkerTooltip() {
        this.markerTooltipVisible = false;
        this.markerTooltipShowToothPositions = false;
        this.markerTooltipKey = '';
        this.markerTooltipEntries = [];
    }

    private getMarkerTooltipKey(target: EventTarget | null): string {
        if (!(target instanceof Element) || !this.getToothLayoutElement()?.contains(target)) return '';
        return target.closest<HTMLElement>('[data-tooth-marker-key]')?.dataset.toothMarkerKey || '';
    }

    private getSurfaceToothPositions(toothPos: string): string[] {
        return /^[1-4][1-5]$/.test(toothPos)
            ? [toothPos, String(Number(toothPos) + 40)]
            : [toothPos];
    }

    private getMarkerTooltipEntries(key: string): ToothMarkerTooltipEntry[] {
        const entries: ToothMarkerTooltipEntry[] = [];
        const addEntry = (entry: ToothMarkerTooltipEntry) => {
            entries.push({ ...entry, layers: [...entry.layers] });
        };

        const parts = key.split('|');
        const isFaceKey = parts[0] === 'face';
        const isSealantKey = parts[0] === 'sealant';
        const toothPositions = isFaceKey || isSealantKey
            ? this.getSurfaceToothPositions(parts[1])
            : [parts[1]];
        const visibleHistoryEntries: ToothMarkerTooltipEntry[] = [];
        toothPositions.forEach(toothPos => {
            const historyKey = isFaceKey ? `face|${toothPos}`
                : isSealantKey ? `sealant|${toothPos}` : key;
            (this.toothMarkerTooltips?.[historyKey] || [])
                .filter(entry => entry.layers.some(layer => this.visibleLayers[layer]))
                .forEach(entry => visibleHistoryEntries.push({ ...entry, toothPos }));
        });
        if (isFaceKey) {
            this.mergeVisibleSurfaceTooltipEntries(visibleHistoryEntries).forEach(addEntry);
        } else {
            visibleHistoryEntries.forEach(addEntry);
        }
        toothPositions.forEach(toothPos => {
            const manualKey = [parts[0], toothPos, ...parts.slice(2)].join('|');
            this.getManualMarkerTooltipEntries(manualKey)
                .forEach(entry => addEntry({ ...entry, toothPos }));
        });

        return entries.sort((first, second) => isFaceKey
            ? second.date.localeCompare(first.date) ||
                toothPositions.indexOf(first.toothPos || '') - toothPositions.indexOf(second.toothPos || '')
            : this.severityOrder[first.severity] - this.severityOrder[second.severity] ||
                first.date.localeCompare(second.date));
    }

    private mergeVisibleSurfaceTooltipEntries(entries: ToothMarkerTooltipEntry[]): ToothMarkerTooltipEntry[] {
        const merged = new Map<string, ToothMarkerTooltipEntry>();
        entries.forEach(entry => {
            if (!entry.procedureCode) {
                merged.set(`unmerged|${merged.size}`, { ...entry, layers: [...entry.layers] });
                return;
            }

            const key = [entry.toothPos, entry.date, entry.procedureCode, entry.source].join('|');
            const existing = merged.get(key);
            if (!existing) {
                merged.set(key, { ...entry, layers: [...entry.layers] });
                return;
            }

            existing.surfaceCodes = Array.from(new Set(
                `${existing.surfaceCodes || ''}${entry.surfaceCodes || ''}`.split('')
            )).join('');
            existing.severity = this.maxSeverity(existing.severity, entry.severity);
            entry.layers.forEach(layer => {
                if (!existing.layers.includes(layer)) existing.layers.push(layer);
            });
        });
        return Array.from(merged.values());
    }

    private getManualMarkerTooltipEntries(key: string): ToothMarkerTooltipEntry[] {
        if (!this.visibleLayers.manual || !this.manualMarkerDate) return [];

        const parts = key.split('|');
        const markerType = parts[0];
        const toothPos = parts[1];
        const annotation = toothPos ? this.manualAnnotations?.[toothPos] : undefined;
        if (!annotation) return [];

        let severity: RuleSeverity | null = null;
        if (markerType === 'face') {
            const face = parts[2];
            if ((annotation.cariesFaces || []).includes(face)) severity = this.maxSeverity(severity, 'info');
            if ((annotation.cantODFaces || []).includes(face) || (annotation.cantSeaFaces || []).includes(face)) {
                severity = this.maxSeverity(severity, 'forbidden');
            }
            if (annotation.veneer && ['c', this.getFacialFace(toothPos)].includes(face)) {
                severity = this.maxSeverity(severity, 'forbidden');
            }
        } else if (markerType === 'tooth') {
            severity = this.getManualToothMarkerSeverity(annotation);
        } else if (markerType === 'sealant') {
            severity = (annotation.cantSeaFaces || []).length > 0 ? 'forbidden' : null;
        }

        return severity
            ? [{ source: 'internal', date: this.manualMarkerDate, severity, layers: ['manual'] }]
            : [];
    }

    private getManualToothMarkerSeverity(annotation: ToothManualAnnotation): RuleSeverity | null {
        let severity: RuleSeverity | null = null;
        const extractedTags: ToothManualTag[] = ['missing', 'extracted', 'implant', 'pontic'];
        const forbiddenTags: ToothManualTag[] = ['crown', 'veneer', 'inlay'];
        const infoTags: ToothManualTag[] = ['impactionTooth', 'residualRoot', 'periodontitis', 'abscess', 'fracture', 'post'];

        extractedTags.forEach(tag => {
            if (annotation[tag]) severity = this.maxSeverity(severity, 'extracted');
        });
        forbiddenTags.forEach(tag => {
            if (annotation[tag]) severity = this.maxSeverity(severity, 'forbidden');
        });
        infoTags.forEach(tag => {
            if (annotation[tag]) severity = this.maxSeverity(severity, 'info');
        });
        return severity;
    }

    hasHistoryLayerMarkers(source: ToothHistoryDataSource): boolean {
        return (this.toothTreatPosLayers?.[source] || []).some(item => {
            const record = item.treatRecord;
            return !!record && (
                (record.cantOD || []).length > 0 ||
                record.canExt === false ||
                record.canENDO === false ||
                record.canSea === false
            );
        });
    }

    setToothFace(facePos: string, isBabyTeeth: boolean) {
        var extPos = facePos + "";
        this.findToothElement(extPos)?.setAttribute('data-tooth-marker-key', `tooth|${extPos}`);
        if (isBabyTeeth) {
            facePos = (Number.parseInt(facePos) - 40).toString();
        }
        const treatRecords = this.getVisibleTreatRecords(extPos);
        var manual = this.visibleLayers.manual ? this.manualAnnotations[extPos] : undefined;
        this.getToothLayoutElement()?.querySelector<SVGElement>(`[id="${facePos}c"]`)
            ?.closest<HTMLElement>('.toothface')?.setAttribute('data-tooth-marker-key', `face|${facePos}`);

        ['c', 'r', 'l', 'u', 'b'].forEach(face => {
            this.setFaceOutlineAttribute(facePos + face, 'data-tooth-marker-key', `face|${facePos}|${face}`);
            var severity = this.getFaceSeverity(treatRecords, face);
            if ((manual?.cantODFaces || []).includes(face) || (manual?.cantSeaFaces || []).includes(face)) {
                severity = this.maxSeverity(severity, 'forbidden');
            }
            if (severity) {
                this.setMarkerColor(facePos + face, severity);
            }
        });

        const sealantSeverity = this.getToothSeverity(treatRecords, 'canSea', 'canSeaSeverity', 'forbidden');
        const finalSealantSeverity = (manual?.cantSeaFaces || []).length
            ? this.maxSeverity(sealantSeverity, 'forbidden')
            : sealantSeverity;
        if (finalSealantSeverity) {
            this.setFaceOutlineAttribute(facePos + 's', 'data-tooth-marker-key', `sealant|${facePos}`);
            this.setMarkerColor(facePos + 's', finalSealantSeverity);
            this.setElementAttribute(facePos + 's', 'fill-opacity', '1');
            this.setElementAttribute(facePos + 's', 'stroke-opacity', '1');
        }

        const extractionSeverity = this.getToothSeverity(treatRecords, 'canExt', 'canExtSeverity', 'extracted');
        if (extractionSeverity) {
            this.setElementAttribute('ext-' + extPos, 'data-tooth-marker-key', `ext|${extPos}`);
            this.setMarkerColor('ext-' + extPos, extractionSeverity);
            this.setElementAttribute('ext-' + extPos, 'fill-opacity', '1');
        }

        const endoSeverity = this.getToothSeverity(treatRecords, 'canENDO', 'canENDOSeverity', 'forbidden');
        if (endoSeverity && !manual?.missing && !manual?.pontic && !manual?.implant) {
            this.renderEndoMarker(extPos, endoSeverity);
        }

        this.applyFaceManualVisuals(facePos, manual);
        this.applyToothManualVisuals(extPos, manual);
    }

    private getVisibleTreatRecords(toothPos: string): toothTreated[] {
        const sources: ToothHistoryDataSource[] = ['internal', 'ic', 'cloud'];
        const hasLayerData = sources.some(source => (this.toothTreatPosLayers?.[source] || []).length > 0);
        if (!hasLayerData) {
            const legacyRecord = this.toothTreatPos.find(item => item.posNo === toothPos)?.treatRecord;
            return legacyRecord ? [legacyRecord] : [];
        }

        return sources
            .filter(source => this.visibleLayers[source])
            .map(source => this.toothTreatPosLayers[source].find(item => item.posNo === toothPos)?.treatRecord)
            .filter((record): record is toothTreated => !!record);
    }

    private getFaceSeverity(records: toothTreated[], face: string): RuleSeverity | null {
        return records.reduce<RuleSeverity | null>((severity, record) => {
            if (!(record.cantOD || []).includes(face)) return severity;
            return this.maxSeverity(severity, record.cantODSeverity?.[face] || 'forbidden');
        }, null);
    }

    private getToothSeverity(
        records: toothTreated[],
        allowedProperty: 'canSea' | 'canExt' | 'canENDO',
        severityProperty: 'canSeaSeverity' | 'canExtSeverity' | 'canENDOSeverity',
        defaultSeverity: RuleSeverity
    ): RuleSeverity | null {
        return records.reduce<RuleSeverity | null>((severity, record) => {
            if (record[allowedProperty] !== false) return severity;
            return this.maxSeverity(severity, record[severityProperty] || defaultSeverity);
        }, null);
    }

    onMapClick(event: MouseEvent) {
        if (this.manualAnnotationDisabled) return;
        const target = event.target as Element;
        const supernumeraryElement = target.closest('.supernumerary-tooth') as HTMLElement | null;
        const supernumeraryToothPos = supernumeraryElement?.dataset.toothPos;
        const supernumeraryFace = (target.closest('[data-supernumerary-face]') as SVGGElement | null)
            ?.dataset.supernumeraryFace;
        if (supernumeraryToothPos && supernumeraryFace) {
            event.stopPropagation();
            this.handleAnnotationTarget('face', supernumeraryToothPos, event, supernumeraryFace);
            return;
        }

        const faceGroup = target.closest('.toothface g[id]');
        const faceMatch = faceGroup?.id.match(/^(\d{2})([crlub])$/);
        if (faceMatch) {
            event.stopPropagation();
            this.handleAnnotationTarget('face', faceMatch[1], event, faceMatch[2]);
            return;
        }

        if (target.closest('.toothface')) return;
        const toothElement = target.closest('.tooth');
        const toothPos = supernumeraryToothPos || toothElement?.querySelector('.position-text')?.textContent?.trim();
        if (!toothPos) return;

        event.stopPropagation();
        this.handleAnnotationTarget('tooth', toothPos, event);
        const tooth = this.toothTreatPos.find(item => item.posNo === toothPos);
        if (tooth) this.toothPosClick.emit(tooth);
    }

    private handleAnnotationTarget(kind: 'face' | 'tooth', toothPos: string, event: MouseEvent, face?: string) {
        if (!this.continuousAnnotation) {
            this.openAnnotationMenu(kind, toothPos, event, face);
            return;
        }

        if (kind === 'face' && this.continuousSurfaceDentition === 'primary') {
            toothPos = this.getSurfaceToothPositions(toothPos)[1] || toothPos;
        }
        if (this.continuousAnnotation.kind === 'face') {
            if (kind === 'face' && face) this.applyContinuousFaceAnnotation(toothPos, face);
        } else {
            this.applyContinuousToothAnnotation(toothPos);
        }
    }

    @HostListener('document:click')
    onDocumentClick() {
        this.annotationMenu = null;
    }

    closeAnnotationMenu(event?: Event) {
        event?.stopPropagation();
        this.annotationMenu = null;
    }

    getAnnotationMenuToothPositions(): string[] {
        const target = this.annotationMenu;
        if (!target || target.kind !== 'face') return [];
        return this.getSurfaceToothPositions(target.surfaceToothPos || target.toothPos);
    }

    selectAnnotationMenuToothPosition(toothPos: string, event: Event) {
        event.stopPropagation();
        if (!this.annotationMenu || !this.getAnnotationMenuToothPositions().includes(toothPos)) return;
        this.annotationMenu = { ...this.annotationMenu, toothPos };
    }

    selectContinuousSurfaceDentition(dentition: SurfaceDentition, event: Event) {
        event.stopPropagation();
        if (this.annotationMenu?.kind === 'continuous') this.continuousSurfaceDentition = dentition;
    }

    isSealantToothPosition(toothPos: string): boolean {
        return /^[1-4][678]$/.test(toothPos || '');
    }

    toggleFaceAnnotation(tag: FaceManualTag, event: Event) {
        event.stopPropagation();
        const target = this.annotationMenu;
        if (!target || target.kind !== 'face' || !target.face) return;
        if (tag === 'cantSea' && !this.isSealantToothPosition(target.toothPos)) return;

        const annotation = this.cloneAnnotation(target.toothPos);
        const faces = tag === 'caries'
            ? annotation.cariesFaces
            : tag === 'cantOD' ? annotation.cantODFaces : annotation.cantSeaFaces;
        const index = faces.indexOf(target.face);
        if (index >= 0) faces.splice(index, 1);
        else faces.push(target.face);
        this.commitManualAnnotation(target.toothPos, annotation);
    }

    toggleToothAnnotation(tag: ToothManualTag, event: Event) {
        event.stopPropagation();
        const target = this.annotationMenu;
        if (!target || target.kind !== 'tooth') return;

        const annotation = this.cloneAnnotation(target.toothPos);
        annotation[tag] = !annotation[tag];
        if (annotation[tag] && tag === 'missing') annotation.extracted = false;
        if (annotation[tag] && tag === 'extracted') annotation.missing = false;
        const next = { ...this.manualAnnotations, [target.toothPos]: annotation };
        if ((tag === 'crown' || tag === 'implant') && !annotation.crown && !annotation.implant) {
            this.removeProstheticConnections(target.toothPos, next);
        }
        this.commitManualAnnotationMap(next);
    }

    toggleContinuousAnnotation(event: MouseEvent) {
        event.stopPropagation();
        if (this.manualAnnotationDisabled) return;
        if (this.continuousAnnotation) {
            this.continuousAnnotation = null;
            this.annotationMenu = null;
            return;
        }
        const menuWidth = 360;
        const menuHeight = 500;
        const viewportMargin = 8;
        const mapRect = this.mapViewport?.nativeElement.getBoundingClientRect();
        const viewportMaxX = Math.max(viewportMargin, window.innerWidth - menuWidth - viewportMargin);
        const mapLeft = mapRect
            ? Math.max(viewportMargin, mapRect.left + viewportMargin)
            : viewportMargin;
        const mapRight = mapRect
            ? Math.min(window.innerWidth - viewportMargin, mapRect.right - viewportMargin)
            : window.innerWidth - viewportMargin;
        const hasHorizontalRoom = mapRight - mapLeft >= menuWidth;
        const menuX = hasHorizontalRoom
            ? Math.max(mapLeft, Math.min(event.clientX - menuWidth / 2, mapRight - menuWidth))
            : Math.min(mapLeft, viewportMaxX);
        const mapTop = mapRect
            ? Math.max(viewportMargin, mapRect.top + viewportMargin)
            : viewportMargin;
        this.annotationMenu = {
            kind: 'continuous',
            toothPos: '',
            x: menuX,
            y: Math.max(mapTop, Math.min(event.clientY + viewportMargin, window.innerHeight - menuHeight))
        };
    }

    selectContinuousFaceAnnotation(tag: FaceManualTag, event: Event) {
        event.stopPropagation();
        this.continuousAnnotation = { kind: 'face', tag };
        this.annotationMenu = null;
    }

    selectContinuousToothAnnotation(tag: ToothManualTag, event: Event) {
        event.stopPropagation();
        this.continuousAnnotation = { kind: 'tooth', tag };
        this.annotationMenu = null;
    }

    getContinuousAnnotationLabel(): string {
        if (!this.continuousAnnotation) return '連續標示';
        const labels: Record<FaceManualTag | ToothManualTag, string> = {
            caries: '齲齒', cantOD: '補牙', cantSea: '溝隙封填',
            missing: '缺牙', extracted: '已拔除', impactionTooth: '埋伏齒/阻生齒', residualRoot: '殘根',
            periodontitis: '牙周', abscess: '膿腫', fracture: '齒裂', crown: '牙冠', pontic: '橋體',
            implant: '植體', post: '釘柱', veneer: '貼片', inlay: '嵌體'
        };
        const targetLabel = this.continuousAnnotation.tag === 'cantSea'
            ? '限大臼齒'
            : `共用牙面：${this.continuousSurfaceDentition === 'primary' ? '乳牙' : '恆牙'}`;
        return `連續標示：${labels[this.continuousAnnotation.tag]}（${targetLabel}）`;
    }

    private applyContinuousFaceAnnotation(toothPos: string, face: string) {
        if (!this.continuousAnnotation || this.continuousAnnotation.kind !== 'face') return;
        if (this.continuousAnnotation.tag === 'cantSea' && !this.isSealantToothPosition(toothPos)) return;
        const annotation = this.cloneAnnotation(toothPos);
        const faces = this.continuousAnnotation.tag === 'caries'
            ? annotation.cariesFaces
            : this.continuousAnnotation.tag === 'cantOD' ? annotation.cantODFaces : annotation.cantSeaFaces;
        if (!faces.includes(face)) faces.push(face);
        this.commitManualAnnotation(toothPos, annotation);
    }

    private applyContinuousToothAnnotation(toothPos: string) {
        if (!this.continuousAnnotation || this.continuousAnnotation.kind !== 'tooth') return;
        const annotation = this.cloneAnnotation(toothPos);
        const tag = this.continuousAnnotation.tag;
        annotation[tag] = true;
        if (tag === 'missing') annotation.extracted = false;
        if (tag === 'extracted') annotation.missing = false;
        this.commitManualAnnotation(toothPos, annotation);
    }

    getProstheticConnectionNeighbors(tag: 'crown' | 'implant'): string[] {
        const target = this.annotationMenu;
        if (!target || target.kind !== 'tooth') return [];
        const annotation = this.manualAnnotations[target.toothPos];
        if (annotation?.pontic || !annotation?.[tag]) return [];
        return this.getAdjacentToothPositions(target.toothPos).filter(toothPos => {
            const neighbor = this.manualAnnotations[toothPos];
            return !neighbor?.pontic && !!(neighbor?.crown || neighbor?.implant);
        });
    }

    isProstheticConnectionSelected(neighborToothPos: string): boolean {
        const target = this.annotationMenu;
        return !!target && this.hasExplicitProstheticConnection(target.toothPos, neighborToothPos);
    }

    setProstheticConnection(neighborToothPos: string, connected: boolean, event: Event) {
        event.stopPropagation();
        const target = this.annotationMenu;
        if (!target || target.kind !== 'tooth') return;

        const next = { ...this.manualAnnotations };
        [target.toothPos, neighborToothPos].forEach((toothPos, index) => {
            const otherToothPos = index === 0 ? neighborToothPos : target.toothPos;
            const annotation = this.cloneAnnotation(toothPos);
            const connections = annotation.prostheticConnections || [];
            annotation.prostheticConnections = connected
                ? Array.from(new Set([...connections, otherToothPos]))
                : connections.filter(item => item !== otherToothPos);
            next[toothPos] = annotation;
        });
        this.commitManualAnnotationMap(next);
    }

    setImpactionRotation(event: Event) {
        event.stopPropagation();
        const target = this.annotationMenu;
        if (!target || target.kind !== 'tooth') return;

        const input = event.target as HTMLInputElement;
        const annotation = this.cloneAnnotation(target.toothPos);
        annotation.impactionAngle = Number(input.value) || 0;
        this.commitManualAnnotation(target.toothPos, annotation);
    }

    getSelectedImpactionAngle(): number {
        const target = this.annotationMenu;
        if (!target || target.kind !== 'tooth') return 0;
        return this.manualAnnotations[target.toothPos]?.impactionAngle || 0;
    }

    clearSelectedManualAnnotations(event: Event) {
        event.stopPropagation();
        const target = this.annotationMenu;
        if (!target) return;

        if (this.supernumeraryTeeth.some(tooth => tooth.id === target.toothPos)) {
            const next = { ...this.manualAnnotations };
            delete next[target.toothPos];
            this.removeProstheticConnections(target.toothPos, next);
            this.supernumeraryTeeth = this.supernumeraryTeeth
                .filter(tooth => tooth.id !== target.toothPos);
            this.annotationMenu = null;
            this.supernumeraryTeethChange.emit([...this.supernumeraryTeeth]);
            this.commitManualAnnotationMap(next);
            return;
        }

        const annotation = this.cloneAnnotation(target.toothPos);
        if (target.kind === 'face' && target.face) {
            annotation.cariesFaces = annotation.cariesFaces.filter(face => face !== target.face);
            annotation.cantODFaces = annotation.cantODFaces.filter(face => face !== target.face);
            annotation.cantSeaFaces = annotation.cantSeaFaces.filter(face => face !== target.face);
        } else {
            this.getToothManualTags().forEach(tag => annotation[tag] = false);
            annotation.impactionAngle = 0;
            annotation.prostheticConnections = [];
        }
        const next = { ...this.manualAnnotations, [target.toothPos]: annotation };
        if (target.kind === 'tooth') this.removeProstheticConnections(target.toothPos, next);
        this.commitManualAnnotationMap(next);
    }

    isFaceTagSelected(tag: FaceManualTag): boolean {
        const target = this.annotationMenu;
        if (!target || target.kind !== 'face' || !target.face) return false;
        const annotation = this.manualAnnotations[target.toothPos];
        const faces = tag === 'caries'
            ? annotation?.cariesFaces
            : tag === 'cantOD' ? annotation?.cantODFaces : annotation?.cantSeaFaces;
        return faces?.includes(target.face) ?? false;
    }

    isToothTagSelected(tag: ToothManualTag): boolean {
        const target = this.annotationMenu;
        return !!(target && target.kind === 'tooth' && this.manualAnnotations[target.toothPos]?.[tag]);
    }

    hasSelectedManualAnnotations(): boolean {
        const target = this.annotationMenu;
        if (!target) return false;
        if (this.supernumeraryTeeth.some(tooth => tooth.id === target.toothPos)) return true;
        const annotation = this.manualAnnotations[target.toothPos];
        if (!annotation) return false;
        if (target.kind === 'face' && target.face) {
            return (annotation.cariesFaces || []).includes(target.face) ||
                (annotation.cantODFaces || []).includes(target.face) ||
                (annotation.cantSeaFaces || []).includes(target.face);
        }
        return this.getToothManualTags().some(tag => !!annotation[tag]);
    }

    private openAnnotationMenu(kind: 'face' | 'tooth', toothPos: string, event: MouseEvent, face?: string) {
        const menuWidth = kind === 'tooth' ? 240 : 220;
        const hasSharedFace = kind === 'face' && this.getSurfaceToothPositions(toothPos).length > 1;
        const menuHeight = kind === 'tooth' ? 390 : hasSharedFace ? 250 : 205;
        this.annotationMenu = {
            kind,
            toothPos,
            surfaceToothPos: kind === 'face' ? toothPos : undefined,
            face,
            x: Math.max(8, Math.min(event.clientX + 8, window.innerWidth - menuWidth)),
            y: Math.max(8, Math.min(event.clientY + 8, window.innerHeight - menuHeight))
        };
    }

    private cloneAnnotation(toothPos: string): ToothManualAnnotation {
        const current = this.manualAnnotations[toothPos];
        const supernumeraryTooth = this.supernumeraryTeeth.find(tooth => tooth.id === toothPos);
        return {
            cariesFaces: [...(current?.cariesFaces || [])],
            cantODFaces: [...(current?.cantODFaces || [])],
            cantSeaFaces: [...(current?.cantSeaFaces || [])],
            missing: current?.missing || false,
            extracted: current?.extracted || false,
            impactionTooth: current?.impactionTooth || false,
            impactionAngle: current?.impactionAngle || 0,
            residualRoot: current?.residualRoot || false,
            periodontitis: current?.periodontitis || false,
            abscess: current?.abscess || false,
            fracture: current?.fracture || false,
            crown: current?.crown || false,
            pontic: current?.pontic || false,
            implant: current?.implant || false,
            prostheticConnections: [...(current?.prostheticConnections || [])],
            post: current?.post || false,
            veneer: current?.veneer || false,
            inlay: current?.inlay || false,
            displayToothPos: current?.displayToothPos || supernumeraryTooth?.displayToothPos,
            isSupernumerary: current?.isSupernumerary || !!supernumeraryTooth
        };
    }

    getAnnotationMenuToothPos(): string {
        const toothPos = this.annotationMenu?.toothPos || '';
        return this.getClinicalToothPos(toothPos);
    }

    private getClinicalToothPos(toothPos: string): string {
        return this.supernumeraryTeeth.find(tooth => tooth.id === toothPos)?.displayToothPos ||
            this.manualAnnotations[toothPos]?.displayToothPos || toothPos;
    }

    private commitManualAnnotation(toothPos: string, annotation: ToothManualAnnotation) {
        const next = { ...this.manualAnnotations };
        next[toothPos] = annotation;
        this.commitManualAnnotationMap(next);
    }

    private commitManualAnnotationMap(next: ToothManualAnnotationMap) {
        Object.keys(next).forEach(toothPos => {
            if (this.isAnnotationEmpty(next[toothPos])) delete next[toothPos];
        });
        this.manualAnnotations = next;
        this.manualAnnotationsChange.emit(next);
        this.setDefaultToothPos();
    }

    private removeProstheticConnections(toothPos: string, annotations: ToothManualAnnotationMap) {
        Object.keys(annotations).forEach(currentToothPos => {
            const annotation = currentToothPos === toothPos
                ? annotations[currentToothPos]
                : { ...annotations[currentToothPos] };
            annotation.prostheticConnections = (annotation.prostheticConnections || [])
                .filter(item => item !== toothPos && currentToothPos !== toothPos);
            annotations[currentToothPos] = annotation;
        });
    }

    private isAnnotationEmpty(annotation: ToothManualAnnotation): boolean {
        return annotation.cariesFaces.length === 0 &&
            annotation.cantODFaces.length === 0 &&
            annotation.cantSeaFaces.length === 0 &&
            !this.getToothManualTags().some(tag => !!annotation[tag]);
    }

    private getToothManualTags(): ToothManualTag[] {
        return [
            'missing', 'extracted', 'impactionTooth', 'residualRoot', 'periodontitis', 'abscess', 'fracture',
            'crown', 'pontic', 'implant', 'post', 'veneer', 'inlay'
        ];
    }

    private getAdjacentToothPositions(toothPos: string): string[] {
        const row = this.toothArchRows.find(items => items.includes(toothPos));
        if (!row) return [];
        const index = row.indexOf(toothPos);
        return [row[index - 1], row[index + 1]].filter((item): item is string => !!item);
    }

    private hasExplicitProstheticConnection(firstToothPos: string, secondToothPos: string): boolean {
        const first = this.manualAnnotations[firstToothPos];
        const second = this.manualAnnotations[secondToothPos];
        return !!(first?.prostheticConnections || []).includes(secondToothPos) ||
            !!(second?.prostheticConnections || []).includes(firstToothPos);
    }

    private isProstheticTooth(annotation?: ToothManualAnnotation): boolean {
        return !!(annotation?.crown || annotation?.pontic || annotation?.implant);
    }

    private areProstheticTeethConnected(firstToothPos: string, secondToothPos: string): boolean {
        const first = this.manualAnnotations[firstToothPos];
        const second = this.manualAnnotations[secondToothPos];
        if (!this.isProstheticTooth(first) || !this.isProstheticTooth(second)) return false;
        return !!(first.pontic || second.pontic) ||
            this.hasExplicitProstheticConnection(firstToothPos, secondToothPos);
    }

    private renderProstheticConnections() {
        const layout = this.getToothLayoutElement();
        if (!layout || !this.visibleLayers.manual) return;

        const renderedPairs = new Set<string>();
        Object.keys(this.manualAnnotations).forEach(toothPos => {
            this.getAdjacentToothPositions(toothPos).forEach(neighborToothPos => {
                const pairKey = [toothPos, neighborToothPos].sort().join(':');
                if (renderedPairs.has(pairKey) || !this.areProstheticTeethConnected(toothPos, neighborToothPos)) return;
                renderedPairs.add(pairKey);
                this.appendProstheticConnector(layout, toothPos, neighborToothPos);
            });
        });
    }

    private appendProstheticConnector(layout: HTMLElement, firstToothPos: string, secondToothPos: string) {
        const firstCrown = this.findToothElement(firstToothPos)
            ?.querySelector<SVGGraphicsElement>('.toothroot > svg > g > path:last-child');
        const secondCrown = this.findToothElement(secondToothPos)
            ?.querySelector<SVGGraphicsElement>('.toothroot > svg > g > path:last-child');
        if (!firstCrown || !secondCrown) return;

        const layoutRect = layout.getBoundingClientRect();
        const firstRect = firstCrown.getBoundingClientRect();
        const secondRect = secondCrown.getBoundingClientRect();
        if (!layoutRect.width || !layoutRect.height) return;

        const scaleX = layout.offsetWidth / layoutRect.width;
        const scaleY = layout.offsetHeight / layoutRect.height;
        const leftRect = firstRect.left <= secondRect.left ? firstRect : secondRect;
        const rightRect = leftRect === firstRect ? secondRect : firstRect;
        const startX = (leftRect.right - layoutRect.left) * scaleX;
        const endX = (rightRect.left - layoutRect.left) * scaleX;
        const startY = (leftRect.top + leftRect.height / 2 - layoutRect.top) * scaleY;
        const endY = (rightRect.top + rightRect.height / 2 - layoutRect.top) * scaleY;
        const deltaX = endX - startX;
        const deltaY = endY - startY;
        const length = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        if (length <= 0) return;

        const connector = document.createElement('div');
        const connectorHeight = 5 / this.getBaseScaleRatios().y;
        connector.className = 'tooth-prosthetic-connector';
        connector.style.position = 'absolute';
        connector.style.left = `${startX}px`;
        connector.style.top = `${startY - connectorHeight / 2}px`;
        connector.style.width = `${length}px`;
        connector.style.height = `${connectorHeight}px`;
        connector.style.borderRadius = `${connectorHeight / 2}px`;
        connector.style.background = 'rgba(53, 173, 114, 0.48)';
        connector.style.pointerEvents = 'none';
        connector.style.transformOrigin = 'left center';
        connector.style.transform = `rotate(${Math.atan2(deltaY, deltaX)}rad)`;
        connector.style.zIndex = '2';
        layout.appendChild(connector);
    }

    onSupernumerarySourcePointerDown(event: PointerEvent) {
        if (this.manualAnnotationDisabled) return;
        event.preventDefault();
        event.stopPropagation();
        const position = this.getLayoutPosition(event.clientX, event.clientY);
        if (!position) return;
        const nearest = '18';
        const tooth: SupernumeraryTooth = {
            id: `supernumerary-${Date.now()}-${++this.supernumerarySequence}`,
            displayToothPos: this.getSupernumeraryDisplayPosition(nearest),
            templateToothPos: nearest,
            betweenToothPositions: this.getSupernumeraryBetweenPositions(nearest, event.clientX, event.clientY),
            x: position.x,
            y: position.y
        };
        this.supernumeraryTeeth = [...this.supernumeraryTeeth, tooth];
        this.startSupernumeraryDrag(tooth.id, event, true);
        this.renderSupernumeraryTeeth();
    }

    @HostListener('document:pointermove', ['$event'])
    onSupernumeraryPointerMove(event: PointerEvent) {
        if (this.manualAnnotationDisabled) {
            this.cancelSupernumeraryDrag();
            return;
        }
        if (!this.supernumeraryDrag || this.supernumeraryDrag.pointerId !== event.pointerId) return;
        event.preventDefault();
        const position = this.getLayoutPosition(event.clientX, event.clientY);
        if (!position) return;
        this.resetSupernumeraryQuadrantLayout();
        const nearest = this.getNearestToothTemplate(event.clientX, event.clientY) || '18';
        this.supernumeraryTeeth = this.supernumeraryTeeth.map(tooth => tooth.id === this.supernumeraryDrag?.id
            ? {
                ...tooth,
                x: position.x,
                y: position.y,
                templateToothPos: nearest,
                betweenToothPositions: this.getSupernumeraryBetweenPositions(nearest, event.clientX, event.clientY),
                displayToothPos: this.getSupernumeraryDisplayPosition(nearest)
            }
            : tooth);
        this.renderSupernumeraryTeeth();
    }

    @HostListener('document:pointerup', ['$event'])
    onSupernumeraryPointerUp(event: PointerEvent) {
        if (this.manualAnnotationDisabled) {
            this.cancelSupernumeraryDrag();
            return;
        }
        const drag = this.supernumeraryDrag;
        if (!drag || drag.pointerId !== event.pointerId) return;
        event.preventDefault();
        event.stopPropagation();
        this.supernumeraryDrag = null;
        this.commitSupernumerarySlotPositions();
        this.renderSupernumeraryTeeth();
        const distance = Math.sqrt(
            Math.pow(event.clientX - drag.startClientX, 2) +
            Math.pow(event.clientY - drag.startClientY, 2));
        this.supernumeraryTeethChange.emit([...this.supernumeraryTeeth]);
        this.synchronizeSupernumeraryAnnotationPosition(drag.id);
        if (distance < 3) {
            this.handleAnnotationTarget(drag.face ? 'face' : 'tooth', drag.id, event, drag.face);
        }
    }

    @HostListener('document:pointercancel', ['$event'])
    onSupernumeraryPointerCancel(event: PointerEvent) {
        const drag = this.supernumeraryDrag;
        if (!drag || drag.pointerId !== event.pointerId) return;
        this.cancelSupernumeraryDrag();
    }

    private cancelSupernumeraryDrag() {
        const drag = this.supernumeraryDrag;
        if (!drag) return;
        this.supernumeraryDrag = null;
        this.supernumeraryTeeth = drag.isNew
            ? this.supernumeraryTeeth.filter(tooth => tooth.id !== drag.id)
            : this.supernumeraryTeeth.map(tooth => tooth.id === drag.id && drag.original ? drag.original : tooth);
        this.renderSupernumeraryTeeth();
    }

    private startSupernumeraryDrag(toothPos: string, event: PointerEvent, isNew: boolean = false) {
        if (this.manualAnnotationDisabled) return;
        const face = (event.target as Element).closest('[data-supernumerary-face]')
            ?.getAttribute('data-supernumerary-face') || undefined;
        const original = this.supernumeraryTeeth.find(tooth => tooth.id === toothPos);
        this.supernumeraryDrag = {
            id: toothPos,
            pointerId: event.pointerId,
            startClientX: event.clientX,
            startClientY: event.clientY,
            face,
            isNew,
            original: original ? { ...original } : undefined
        };
    }

    private synchronizeSupernumeraryAnnotationPosition(toothPos: string) {
        const tooth = this.supernumeraryTeeth.find(item => item.id === toothPos);
        const current = this.manualAnnotations[toothPos];
        if (!tooth || !current) return;
        const next = {
            ...this.manualAnnotations,
            [toothPos]: { ...current, displayToothPos: tooth.displayToothPos, isSupernumerary: true }
        };
        this.manualAnnotations = next;
        this.manualAnnotationsChange.emit(next);
    }

    private getLayoutPosition(clientX: number, clientY: number): { x: number; y: number } | null {
        const layout = this.getToothLayoutElement();
        if (!layout) return null;
        const rect = layout.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        return {
            x: Math.max(0, Math.min(layout.offsetWidth, (clientX - rect.left) * layout.offsetWidth / rect.width)),
            y: Math.max(0, Math.min(layout.offsetHeight, (clientY - rect.top) * layout.offsetHeight / rect.height))
        };
    }

    private getNearestToothTemplate(clientX: number, clientY: number): string | null {
        const layout = this.getToothLayoutElement();
        if (!layout) return null;
        let nearestToothPos: string | null = null;
        let nearestDistance = Number.POSITIVE_INFINITY;
        layout.querySelectorAll<HTMLElement>('#upper-teeth .tooth, #lower-teeth .tooth').forEach(tooth => {
            if (!tooth.querySelector('.toothroot')) return;
            const toothPos = tooth.querySelector('.position-text')?.textContent?.trim();
            if (!toothPos) return;
            const rect = tooth.getBoundingClientRect();
            const distance = Math.pow(clientX - (rect.left + rect.width / 2), 2) +
                Math.pow(clientY - (rect.top + rect.height / 2), 2);
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestToothPos = toothPos;
            }
        });
        return nearestToothPos;
    }

    private getSupernumeraryDisplayPosition(templateToothPos: string): '19' | '29' | '39' | '49' {
        const quadrant = Number(templateToothPos.charAt(0));
        if (quadrant === 2 || quadrant === 6) return '29';
        if (quadrant === 3 || quadrant === 7) return '39';
        if (quadrant === 4 || quadrant === 8) return '49';
        return '19';
    }

    private getSupernumeraryBetweenPositions(
        nearestToothPos: string,
        clientX: number,
        clientY: number
    ): [string, string] | undefined {
        const row = this.toothQuadrants.find(items => items.includes(nearestToothPos));
        if (!row) return undefined;
        const index = row.indexOf(nearestToothPos);
        const nearest = this.getToothTemplateElements(nearestToothPos).root;
        const nearestRect = nearest?.getBoundingClientRect();
        if (nearestRect) {
            const centerX = nearestRect.left + nearestRect.width / 2;
            const isBeforeFirst = index === 0 && clientX < centerX;
            const isAfterLast = index === row.length - 1 && clientX > centerX;
            if (isBeforeFirst || isAfterLast) {
                return this.crossMidlinePairs[nearestToothPos];
            }
        }
        const adjacent = [row[index - 1], row[index + 1]].filter((item): item is string => !!item);
        if (adjacent.length === 0) return undefined;
        const neighbor = adjacent.reduce<{ toothPos: string; distance: number } | null>((closest, toothPos) => {
            const tooth = this.getToothTemplateElements(toothPos).root;
            const rect = tooth?.getBoundingClientRect();
            if (!rect) return closest;
            const distance = Math.pow(clientX - (rect.left + rect.width / 2), 2) +
                Math.pow(clientY - (rect.top + rect.height / 2), 2);
            return !closest || distance < closest.distance ? { toothPos, distance } : closest;
        }, null)?.toothPos;
        if (!neighbor) return undefined;
        return row.indexOf(neighbor) < index
            ? [neighbor, nearestToothPos]
            : [nearestToothPos, neighbor];
    }

    private captureSupernumeraryTemplates() {
        const layout = this.getToothLayoutElement();
        if (!layout) return;

        this.supernumeraryTemplates.clear();
        const templatePositions = Array.from(new Set<string>(
            this.toothArchRows.reduce<string[]>((positions, row) => positions.concat(row), [])
        ));
        templatePositions.forEach(toothPos => {
            const template = this.getToothTemplateElements(toothPos);
            if (!template.root) return;

            const faceTemplatePos = Number(toothPos) > 40
                ? (Number(toothPos) - 40).toString()
                : toothPos;
            const face = template.face || this.getToothTemplateElements(faceTemplatePos).face;
            this.supernumeraryTemplates.set(toothPos, {
                root: template.root.cloneNode(true) as HTMLElement,
                face: face?.cloneNode(true) as HTMLElement,
                width: template.root.offsetWidth || 58
            });
        });
    }

    private resetSupernumeraryQuadrantLayout() {
        const layout = this.getToothLayoutElement();
        const containers = new Set<HTMLElement>();
        layout?.querySelectorAll<HTMLElement>(
            '#upper-teeth .adult-teeth > .tooth, #upper-teeth .baby-teeth > .tooth, ' +
            '#lower-teeth .adult-teeth > .tooth, #lower-teeth .baby-teeth > .tooth')
            .forEach(tooth => {
                if (tooth.parentElement) containers.add(tooth.parentElement);
                tooth.classList.remove('tooth--supernumerary-compressed');
                tooth.style.removeProperty('flex');
                tooth.style.removeProperty('width');
                tooth.style.removeProperty('transform');
                tooth.style.removeProperty('margin-left');
                tooth.style.removeProperty('--supernumerary-tooth-scale-x');
            });
        containers.forEach(container => {
            container.style.removeProperty('box-sizing');
            container.style.removeProperty('width');
            container.style.removeProperty('justify-content');
            delete container.dataset.supernumeraryBaseSlotWidth;
        });
    }

    private applySupernumeraryQuadrantLayout(): Map<string, SupernumerarySlot> {
        const layout = this.getToothLayoutElement();
        const slots = new Map<string, SupernumerarySlot>();
        if (!layout) return slots;

        this.toothQuadrants.forEach(positions => {
            const teeth = positions
                .map(toothPos => this.getToothTemplateElements(toothPos).root)
                .filter((tooth): tooth is HTMLElement => !!tooth);
            const container = teeth[0]?.parentElement;
            if (!container || teeth.length !== positions.length) return;

            teeth.forEach(tooth => {
                tooth.classList.remove('tooth--supernumerary-compressed');
                tooth.style.removeProperty('flex');
                tooth.style.removeProperty('width');
                tooth.style.removeProperty('transform');
                tooth.style.removeProperty('margin-left');
                tooth.style.removeProperty('--supernumerary-tooth-scale-x');
            });
            container.style.removeProperty('box-sizing');
            container.style.removeProperty('width');
            container.style.removeProperty('justify-content');

            const supernumerary = this.supernumeraryTeeth
                .filter(tooth => positions.includes(tooth.templateToothPos))
                .sort((first, second) => {
                    const firstIndex = this.getSupernumeraryInsertionIndex(first, positions);
                    const secondIndex = this.getSupernumeraryInsertionIndex(second, positions);
                    return firstIndex === secondIndex ? first.x - second.x : firstIndex - secondIndex;
                });
            if (supernumerary.length === 0) return;

            const baseContainerWidth = Number(container.dataset.supernumeraryBaseWidth) || container.offsetWidth;
            container.dataset.supernumeraryBaseWidth = `${baseContainerWidth}`;
            const styles = window.getComputedStyle(container);
            const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
            const borderLeft = Number.parseFloat(styles.borderLeftWidth) || 0;
            const naturalContainerRect = container.getBoundingClientRect();
            const naturalFirstToothRect = teeth[0].getBoundingClientRect();
            const containerScale = naturalContainerRect.width
                ? container.offsetWidth / naturalContainerRect.width
                : 1;
            const baseRowLeft = (naturalFirstToothRect.left - naturalContainerRect.left) * containerScale;
            const baseSlotWidth = teeth.reduce((total, tooth) => total + tooth.offsetWidth, 0) / positions.length;
            const slotWidth = baseSlotWidth * positions.length / (positions.length + supernumerary.length);
            const toothScale = Math.min(1, slotWidth / baseSlotWidth);

            container.style.boxSizing = 'border-box';
            container.style.width = `${baseContainerWidth}px`;
            container.style.justifyContent = 'flex-start';

            teeth.forEach(tooth => {
                tooth.classList.add('tooth--supernumerary-compressed');
                tooth.style.flex = `0 0 ${slotWidth}px`;
                tooth.style.width = `${slotWidth}px`;
                tooth.style.setProperty('--supernumerary-tooth-scale-x', `${toothScale}`);
            });
            teeth[0].style.marginLeft = `${Math.max(0, baseRowLeft - borderLeft - paddingLeft)}px`;

            const insertionGroups = new Map<number, SupernumeraryTooth[]>();
            supernumerary.forEach(tooth => {
                const insertionIndex = this.getSupernumeraryInsertionIndex(tooth, positions);
                const group = insertionGroups.get(Math.max(0, insertionIndex)) || [];
                group.push(tooth);
                insertionGroups.set(Math.max(0, insertionIndex), group);
            });

            const ordered: Array<string | SupernumeraryTooth> = [];
            positions.forEach((toothPos, index) => {
                (insertionGroups.get(index) || []).forEach(tooth => ordered.push(tooth));
                ordered.push(toothPos);
            });
            (insertionGroups.get(positions.length) || []).forEach(tooth => ordered.push(tooth));
            ordered.forEach((item, slotIndex) => {
                if (typeof item !== 'string') return;
                const toothIndex = positions.indexOf(item);
                const tooth = teeth[toothIndex];
                tooth.style.transform = `translateX(${(slotIndex - toothIndex) * slotWidth}px)`;
            });

            const layoutRect = layout.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            if (!layoutRect.width || !containerRect.width) return;
            const layoutScaleX = layout.offsetWidth / layoutRect.width;
            const contentLeft = (containerRect.left - layoutRect.left) * layoutScaleX + baseRowLeft;
            ordered.forEach((item, index) => {
                if (typeof item === 'string') return;
                slots.set(item.id, {
                    x: contentLeft + slotWidth * (index + 0.5),
                    width: baseSlotWidth,
                    scale: toothScale
                });
            });
        });

        return slots;
    }

    private getSupernumeraryInsertionIndex(tooth: SupernumeraryTooth, positions: string[]): number {
        const between = tooth.betweenToothPositions;
        const firstIndex = between ? positions.indexOf(between[0]) : -1;
        const secondIndex = between ? positions.indexOf(between[1]) : -1;
        if (firstIndex >= 0 && secondIndex >= 0) return Math.max(firstIndex, secondIndex);
        if (firstIndex >= 0 || secondIndex >= 0) {
            const localIndex = Math.max(firstIndex, secondIndex);
            return localIndex === 0 ? 0 : localIndex + 1;
        }

        const templateIndex = positions.indexOf(tooth.templateToothPos);
        const templateCenterX = this.getToothCenterXInLayout(tooth.templateToothPos);
        if (templateIndex === 0 && templateCenterX !== null && tooth.x < templateCenterX) return 0;
        if (templateIndex === positions.length - 1 && templateCenterX !== null && tooth.x > templateCenterX) {
            return positions.length;
        }
        return Math.min(positions.length, Math.max(0, templateIndex + 1));
    }

    private getToothCenterXInLayout(toothPos: string): number | null {
        const layout = this.getToothLayoutElement();
        const tooth = this.getToothTemplateElements(toothPos).root;
        if (!layout || !tooth) return null;
        const layoutRect = layout.getBoundingClientRect();
        const toothRect = tooth.getBoundingClientRect();
        if (!layoutRect.width) return null;
        return (toothRect.left + toothRect.width / 2 - layoutRect.left) * layout.offsetWidth / layoutRect.width;
    }

    private commitSupernumerarySlotPositions() {
        const slots = this.applySupernumeraryQuadrantLayout();
        this.supernumeraryTeeth = this.supernumeraryTeeth.map(tooth => {
            const slot = slots.get(tooth.id);
            // 象限外側沒有 betweenToothPositions，必須保留放手時的原始 x，
            // 才能在後續重排時持續判斷位於第一／最後一顆牙的哪一側。
            return slot && tooth.betweenToothPositions ? { ...tooth, x: slot.x } : tooth;
        });
    }

    private renderSupernumeraryTeeth() {
        const source = this.supernumerarySource?.nativeElement;
        const layer = this.supernumeraryLayer?.nativeElement;
        if (!source || !layer) return;
        source.innerHTML = '';
        layer.innerHTML = '';

        const sourceVisual = this.createSupernumeraryVisual({
            id: 'supernumerary-source', displayToothPos: '19', templateToothPos: '18', x: 0, y: 0
        }, false, false);
        if (sourceVisual) {
            const sourceLabel = sourceVisual.querySelector<HTMLElement>('.position-text');
            if (sourceLabel) sourceLabel.textContent = '多生牙';
            source.appendChild(sourceVisual);
        }
        this.resetSupernumeraryQuadrantLayout();
        this.positionSupernumerarySource();

        const slots = this.applySupernumeraryQuadrantLayout();
        this.supernumeraryTeeth.forEach(tooth => {
            const visual = this.createSupernumeraryVisual(tooth, true);
            if (!visual) return;
            const slot = slots.get(tooth.id);
            const isDragged = this.supernumeraryDrag?.id === tooth.id;
            visual.style.left = `${!isDragged && slot ? slot.x : tooth.x}px`;
            visual.style.top = `${tooth.y}px`;
            if (slot) {
                visual.style.width = `${slot.width}px`;
                visual.style.transform = `translate(-50%, -50%) scaleX(${slot.scale})`;
            }
            layer.appendChild(visual);
            this.applySupernumeraryManualVisuals(tooth.id);
        });
    }

    private createSupernumeraryVisual(
        tooth: SupernumeraryTooth,
        draggable: boolean,
        includeFace: boolean = true
    ): HTMLElement | null {
        const template = this.supernumeraryTemplates.get(tooth.templateToothPos);
        if (!template || (includeFace && !template.face)) return null;
        const wrapper = template.root.cloneNode(false) as HTMLElement;
        wrapper.className = 'tooth supernumerary-tooth';
        wrapper.dataset.toothPos = tooth.id;
        wrapper.dataset.toothMarkerKey = `tooth|${tooth.id}`;
        wrapper.style.position = draggable ? 'absolute' : 'relative';
        wrapper.style.display = 'flex';
        wrapper.style.flexDirection = 'column';
        wrapper.style.alignItems = 'center';
        wrapper.style.width = `${template.width}px`;
        wrapper.style.transform = draggable ? 'translate(-50%, -50%)' : '';
        wrapper.style.cursor = 'grab';
        wrapper.style.pointerEvents = 'auto';
        wrapper.style.touchAction = 'none';
        wrapper.style.zIndex = '6';

        const labelSource = template.root.querySelector<HTMLElement>('.position-text') ||
            template.face?.querySelector<HTMLElement>('.position-text');
        const label = labelSource
            ? labelSource.cloneNode(true) as HTMLElement
            : document.createElement('div');
        label.className = 'position-text';
        label.textContent = tooth.displayToothPos;

        const rootContainer = template.root.querySelector<HTMLElement>('.toothroot')?.parentElement?.cloneNode(true) as HTMLElement;
        if (!rootContainer) return null;
        rootContainer.querySelectorAll('.extLayout, .endo-Layout').forEach(element => element.remove());
        rootContainer.style.height = '100px';
        rootContainer.style.position = 'relative';

        const face = includeFace
            ? template.face?.querySelector<HTMLElement>('.toothface')?.cloneNode(true) as HTMLElement
            : null;
        face?.querySelectorAll<SVGGElement>('g[id$="s"]').forEach(group => group.remove());
        face?.querySelectorAll<SVGElement>('#uBorder, #rBorder, #bBorder, #lBorder, #cBorder')
            .forEach(border => border.remove());
        face?.querySelectorAll<SVGGElement>('g[id]').forEach(group => {
            const match = group.id.match(/([crlub])$/);
            if (!match) return;
            group.dataset.supernumeraryFace = match[1];
            group.setAttribute('fill', '#FFFFFF');
            group.setAttribute('fill-opacity', '1');
            group.setAttribute('stroke-opacity', '0.32');
        });
        face?.querySelectorAll<SVGElement>('[id]').forEach(element => element.removeAttribute('id'));

        const isUpper = ['1', '2', '5', '6'].includes(tooth.templateToothPos.charAt(0));
        if (!face) wrapper.append(label, rootContainer);
        else if (isUpper) wrapper.append(label, rootContainer, face);
        else wrapper.append(face, rootContainer, label);

        if (draggable) {
            wrapper.addEventListener('pointerdown', event => {
                event.preventDefault();
                event.stopPropagation();
                this.startSupernumeraryDrag(tooth.id, event);
            });
            wrapper.addEventListener('click', event => event.stopPropagation());
        }
        return wrapper;
    }

    private positionSupernumerarySource() {
        const sourceTools = this.supernumerarySource?.nativeElement.parentElement;
        const layout = this.getToothLayoutElement();
        const topAnchor = this.getToothTemplateElements('55').root;
        const leftAnchor = this.getToothTemplateElements('17').root;
        if (!sourceTools || !layout || !topAnchor || !leftAnchor) return;

        const layoutRect = layout.getBoundingClientRect();
        const topRect = topAnchor.getBoundingClientRect();
        const leftRect = leftAnchor.getBoundingClientRect();
        if (!layoutRect.width || !layoutRect.height) return;

        sourceTools.style.top = `${(topRect.top - layoutRect.top) * layout.offsetHeight / layoutRect.height}px`;
        sourceTools.style.left = `${(leftRect.left - layoutRect.left) * layout.offsetWidth / layoutRect.width}px`;
    }

    private getToothTemplateElements(toothPos: string): { root?: HTMLElement; face?: HTMLElement } {
        const teeth = Array.from(this.getToothLayoutElement()
            ?.querySelectorAll<HTMLElement>('#upper-teeth .tooth, #lower-teeth .tooth') || []);
        const matchesPosition = (element: HTMLElement) =>
            element.querySelector('.position-text')?.textContent?.trim() === toothPos;
        return {
            root: teeth.find(element => matchesPosition(element) && !!element.querySelector('.toothroot')),
            face: teeth.find(element => matchesPosition(element) && !!element.querySelector('.toothface'))
        };
    }

    private applySupernumeraryManualVisuals(toothPos: string) {
        const annotation = this.visibleLayers.manual ? this.manualAnnotations[toothPos] : undefined;
        if (!annotation) return;
        const tooth = this.findToothElement(toothPos);
        if (!tooth) return;
        const setFaceFill = (face: string, fill: string, opacity: string) => {
            const group = tooth.querySelector<SVGGElement>(`[data-supernumerary-face="${face}"]`);
            group?.setAttribute('data-tooth-marker-key', `face|${toothPos}|${face}`);
            group?.setAttribute('fill', fill);
            group?.setAttribute('fill-opacity', opacity);
            group?.setAttribute('data-manual-marked', 'true');
            group?.querySelectorAll<SVGElement>('[fill]').forEach(child => child.setAttribute('fill', fill));
        };
        (annotation.cariesFaces || []).forEach(face => setFaceFill(face, '#1c1c1c', '0.45'));
        (annotation.cantODFaces || []).forEach(face => setFaceFill(face, '#c0392b', '1'));
        (annotation.cantSeaFaces || []).forEach(face => setFaceFill(face, '#c0392b', '1'));
        if (annotation.veneer) {
            ['c', this.getFacialFace(toothPos)].forEach(face =>
                setFaceFill(face, 'url(#tooth-green-hatch)', '1'));
        }
        this.applyToothManualVisuals(toothPos, annotation);
    }

    private applyFaceManualVisuals(facePos: string, annotation?: ToothManualAnnotation) {
        if (!annotation) return;

        (annotation.cariesFaces || []).forEach(face => {
            this.setElementFill(facePos + face, '#1c1c1c');
            this.setElementAttribute(facePos + face, 'fill-opacity', '0.45');
            this.setFaceOutlineAttribute(facePos + face, 'data-manual-marked', 'true');
        });

        if (annotation.veneer) {
            ['c', this.getFacialFace(facePos)].forEach(face => {
                this.setElementFill(facePos + face, 'url(#tooth-green-hatch)');
                this.setElementAttribute(facePos + face, 'fill-opacity', '1');
                this.setFaceOutlineAttribute(facePos + face, 'data-manual-marked', 'true');
            });
        }
    }

    private getFacialFace(toothPos: string): 'u' | 'b' {
        const quadrant = Number(this.getClinicalToothPos(toothPos).charAt(0));
        return [1, 2, 5, 6].includes(quadrant) ? 'u' : 'b';
    }

    private applyToothManualVisuals(toothPos: string, annotation?: ToothManualAnnotation) {
        if (!annotation) return;
        const toothElement = this.findToothElement(toothPos);
        const toothRoot = toothElement?.querySelector<HTMLElement>('.toothroot');
        const anatomyContainer = toothRoot?.parentElement;
        if (!toothElement || !toothRoot || !anatomyContainer) return;

        anatomyContainer.classList.add('tooth-anatomy-container');
        const isUpper = ['1', '2', '5', '6'].includes(this.getClinicalToothPos(toothPos).charAt(0));
        const geometry = this.getToothAnatomyGeometry(anatomyContainer, isUpper);
        toothElement.style.setProperty('--cervical-x', `${geometry.cervicalX}px`);
        toothElement.style.setProperty('--cervical-y', `${geometry.cervicalY}px`);
        toothElement.style.setProperty('--impaction-angle', `${annotation.impactionAngle || 0}deg`);

        const classMap: Array<{ enabled: boolean; className: string }> = [
            { enabled: annotation.missing, className: 'manual-missing' },
            { enabled: annotation.extracted, className: 'manual-extracted' },
            { enabled: annotation.impactionTooth, className: 'manual-impaction' },
            { enabled: annotation.residualRoot, className: 'manual-residual-root' },
            { enabled: annotation.periodontitis, className: 'manual-periodontitis' },
            { enabled: annotation.abscess, className: 'manual-abscess' },
            { enabled: annotation.fracture, className: 'manual-fracture' },
            { enabled: annotation.crown, className: 'manual-crown' },
            { enabled: annotation.pontic, className: 'manual-pontic' },
            { enabled: annotation.implant, className: 'manual-implant' },
            { enabled: annotation.post, className: 'manual-post' },
            { enabled: annotation.veneer, className: 'manual-veneer' },
            { enabled: annotation.inlay, className: 'manual-inlay' }
        ];
        classMap.forEach(item => {
            if (item.enabled) toothElement.classList.add(item.className);
        });

        if (annotation.abscess) this.appendAnatomyOverlay(toothRoot, 'abscess', geometry);
        if (annotation.fracture) this.appendAnatomyOverlay(toothRoot, 'fracture', geometry);
        if (annotation.implant) this.appendAnatomyOverlay(toothRoot, 'implant', geometry);
        if (annotation.post) this.appendAnatomyOverlay(toothRoot, 'post', geometry);
    }

    private getToothAnatomyGeometry(container: HTMLElement, isUpper: boolean): ToothAnatomyGeometry {
        const fallback: ToothAnatomyGeometry = {
            cervicalX: 24.5,
            cervicalY: isUpper ? 69 : 27,
            overlayAxisX: 24.5,
            overlayCervicalY: isUpper ? 69 : 27,
            crownMidpointY: isUpper ? 81.5 : 15.5,
            rootApexX: 24.5,
            rootApexY: isUpper ? 9 : 87,
            longAxisStartY: 4,
            longAxisEndY: 94,
            viewBox: '0 0 49 96',
            overlayWidth: 49,
            overlayHeight: 96,
            svgScaleX: 1,
            svgScaleY: 1
        };
        const svg = container.querySelector<SVGSVGElement>('.toothroot > svg');
        const group = svg?.querySelector<SVGGElement>('g');
        const paths = group ? Array.from(group.children) as SVGGraphicsElement[] : [];
        const rootPath = paths[0];
        const crownPath = paths[paths.length - 1];
        const containerRect = container.getBoundingClientRect();
        if (!rootPath || !crownPath || !containerRect.width || !containerRect.height) return fallback;

        const rootRect = rootPath.getBoundingClientRect();
        const crownRect = crownPath.getBoundingClientRect();
        const rootBox = rootPath.getBBox();
        const crownBox = crownPath.getBBox();
        const viewBox = svg.viewBox.baseVal;
        if (!viewBox.width || !viewBox.height) return fallback;
        const scaleX = container.offsetWidth / containerRect.width;
        const scaleY = container.offsetHeight / containerRect.height;
        const localX = (screenX: number) => (screenX - containerRect.left) * scaleX;
        const localY = (screenY: number) => (screenY - containerRect.top) * scaleY;
        const overlayWidth = svg.clientWidth || 49;
        const overlayHeight = svg.clientHeight || 90;
        const overlayCervicalY = isUpper ? crownBox.y : crownBox.y + crownBox.height;
        const crownEdgeY = isUpper ? crownBox.y + crownBox.height : crownBox.y;

        return {
            cervicalX: localX(crownRect.left + crownRect.width / 2),
            cervicalY: localY(isUpper ? crownRect.top : crownRect.bottom),
            overlayAxisX: crownBox.x + crownBox.width / 2,
            overlayCervicalY,
            crownMidpointY: (overlayCervicalY + crownEdgeY) / 2,
            rootApexX: rootBox.x + rootBox.width / 2,
            rootApexY: isUpper ? rootBox.y : rootBox.y + rootBox.height,
            longAxisStartY: Math.min(rootBox.y, crownBox.y),
            longAxisEndY: Math.max(rootBox.y + rootBox.height, crownBox.y + crownBox.height),
            viewBox: `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`,
            overlayWidth,
            overlayHeight,
            svgScaleX: overlayWidth / viewBox.width,
            svgScaleY: overlayHeight / viewBox.height
        };
    }

    private getFracturePoints(geometry: ToothAnatomyGeometry): string {
        const segments = 10;
        const toothLength = geometry.longAxisEndY - geometry.longAxisStartY;
        return Array.from({ length: segments + 1 }, (_, index) => {
            const y = geometry.longAxisStartY + toothLength * index / segments;
            const isEnd = index === 0 || index === segments;
            const x = geometry.overlayAxisX + (isEnd ? 0 : index % 2 === 0 ? 3 : -3);
            return `${x.toFixed(2)},${y.toFixed(2)}`;
        }).join(' ');
    }

    private getImplantPaths(geometry: ToothAnatomyGeometry): { body: string; threads: string } {
        const cervicalY = geometry.overlayCervicalY;
        const apexY = geometry.rootApexY;
        const rootDirection = apexY < cervicalY ? -1 : 1;
        const rootLength = Math.abs(apexY - cervicalY);
        const halfWidth = 5;
        const tipBaseY = apexY - rootDirection * Math.min(7, rootLength * 0.15);
        const left = geometry.overlayAxisX - halfWidth;
        const right = geometry.overlayAxisX + halfWidth;
        const body = `M${left} ${cervicalY} L${right} ${cervicalY} L${geometry.overlayAxisX + 3} ${tipBaseY} L${geometry.overlayAxisX} ${apexY} L${geometry.overlayAxisX - 3} ${tipBaseY} Z`;
        const threads = Array.from({ length: 6 }, (_, index) => {
            const y = cervicalY + (apexY - cervicalY) * (index + 1) / 7;
            return `M${left} ${y - rootDirection * 1.5} L${right} ${y + rootDirection * 1.5}`;
        }).join(' ');
        return { body, threads };
    }

    private renderEndoMarker(toothPos: string, severity: RuleSeverity) {
        const markerId = 'endo' + toothPos;
        const marker = this.getToothLayoutElement()?.querySelector<SVGElement>(`[id="${markerId}"]`);
        if (marker) {
            marker.setAttribute('data-tooth-marker-key', `endo|${toothPos}`);
            this.setMarkerColor(markerId, severity);
            marker.setAttribute('fill-opacity', '1');
            return;
        }

        const toothElement = this.findToothElement(toothPos);
        const toothRoot = toothElement?.querySelector<HTMLElement>('.toothroot');
        const anatomyContainer = toothRoot?.parentElement;
        if (!toothElement || !toothRoot || !anatomyContainer) return;

        const rootSvg = toothRoot.querySelector<SVGSVGElement>('svg');
        const rootPath = rootSvg?.querySelector<SVGPathElement>('g > path');
        const pathData = rootPath?.getAttribute('d');
        const viewBox = rootSvg?.getAttribute('viewBox');
        if (!rootSvg || !rootPath || !pathData || !viewBox) return;

        let rootBox: { x: number; y: number; width: number; height: number };
        try {
            rootBox = rootPath.getBBox();
        } catch {
            return;
        }
        if (![rootBox.x, rootBox.y, rootBox.width, rootBox.height].every(Number.isFinite) ||
            rootBox.width <= 0 || rootBox.height <= 0) return;

        anatomyContainer.classList.add('tooth-anatomy-container');
        const isUpper = ['1', '2', '5', '6'].includes(this.getClinicalToothPos(toothPos).charAt(0));
        const horizontalScale = 0.68;
        const longitudinalScale = 0.75;
        const apexInsetRatio = 0.06;
        const cervicalInsetRatio = 1 - longitudinalScale - apexInsetRatio;
        const targetX = rootBox.x + rootBox.width * (1 - horizontalScale) / 2;
        const targetY = rootBox.y + rootBox.height * (isUpper ? apexInsetRatio : cervicalInsetRatio);
        const translateX = targetX - rootBox.x * horizontalScale;
        const translateY = targetY - rootBox.y * longitudinalScale;
        const overlayWidth = rootSvg.clientWidth || Number(rootSvg.getAttribute('width'));
        const overlayHeight = rootSvg.clientHeight || Number(rootSvg.getAttribute('height'));
        if (!overlayWidth || !overlayHeight) return;

        const overlay = document.createElement('div');
        overlay.className = 'tooth-anatomy-overlay tooth-anatomy-overlay--endo';
        overlay.dataset.markerSeverity = severity;
        overlay.dataset.toothMarkerKey = `endo|${toothPos}`;
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = `${overlayWidth}px`;
        overlay.style.height = `${overlayHeight}px`;
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '3';

        const markerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        markerSvg.setAttribute('viewBox', viewBox);
        const rootTransform = rootSvg.getAttribute('transform');
        if (rootTransform) markerSvg.setAttribute('transform', rootTransform);
        markerSvg.setAttribute('aria-hidden', 'true');
        markerSvg.setAttribute('focusable', 'false');
        markerSvg.style.display = 'block';
        markerSvg.style.width = '100%';
        markerSvg.style.height = '100%';
        markerSvg.style.overflow = 'visible';
        const markerPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        markerPath.setAttribute('d', pathData);
        markerPath.setAttribute('fill', this.severityColors[severity]);
        markerPath.setAttribute('fill-opacity', '1');
        markerPath.setAttribute('stroke', 'none');
        markerPath.setAttribute(
            'transform',
            `matrix(${horizontalScale} 0 0 ${longitudinalScale} ${translateX} ${translateY})`
        );
        const fillRule = rootPath.getAttribute('fill-rule');
        if (fillRule) markerPath.setAttribute('fill-rule', fillRule);
        markerSvg.appendChild(markerPath);
        overlay.appendChild(markerSvg);
        toothRoot.appendChild(overlay);
    }

    private appendAnatomyOverlay(
        toothRoot: HTMLElement,
        kind: 'abscess' | 'fracture' | 'implant' | 'post',
        geometry: ToothAnatomyGeometry
    ) {
        const overlay = document.createElement('div');
        overlay.className = `tooth-anatomy-overlay tooth-anatomy-overlay--${kind}`;
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = `${geometry.overlayWidth}px`;
        overlay.style.height = `${geometry.overlayHeight}px`;
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = kind === 'abscess' ? '0' : kind === 'fracture' ? '5' : kind === 'implant' ? '2' : '3';
        const svgStart = `<svg viewBox="${geometry.viewBox}" aria-hidden="true" style="display:block;width:100%;height:100%;overflow:visible">`;

        if (kind === 'abscess') {
            const baseScale = this.getBaseScaleRatios();
            const radiusX = 5 / baseScale.x / geometry.svgScaleX;
            const radiusY = 5 / baseScale.y / geometry.svgScaleY;
            overlay.innerHTML = `${svgStart}<ellipse cx="${geometry.rootApexX}" cy="${geometry.rootApexY}" rx="${radiusX}" ry="${radiusY}" fill="#111111" /></svg>`;
        } else if (kind === 'fracture') {
            overlay.innerHTML = `${svgStart}<polyline points="${this.getFracturePoints(geometry)}" fill="none" stroke="#111111" stroke-width="2" stroke-linejoin="miter" /></svg>`;
        } else if (kind === 'implant') {
            const implant = this.getImplantPaths(geometry);
            overlay.innerHTML = `${svgStart}<path d="${implant.body}" fill="#35ad72" fill-opacity="0.48" stroke="#147a4d" stroke-width="1.5"/><path d="${implant.threads}" fill="none" stroke="#147a4d" stroke-width="1.5"/></svg>`;
        } else {
            const postStrokeWidth = 5 / this.getBaseScaleRatios().x / geometry.svgScaleX;
            overlay.innerHTML = `${svgStart}<line x1="${geometry.overlayAxisX}" y1="${geometry.crownMidpointY}" x2="${geometry.overlayAxisX}" y2="${geometry.rootApexY}" stroke="#15945c" stroke-width="${postStrokeWidth}" stroke-linecap="round" /></svg>`;
        }
        toothRoot.appendChild(overlay);
    }

    private findToothElement(toothPos: string): HTMLElement | undefined {
        return Array.from(this.getToothLayoutElement()?.querySelectorAll<HTMLElement>('.tooth') || [])
            .find(element => element.dataset.toothPos === toothPos ||
                element.querySelector('.position-text')?.textContent?.trim() === toothPos);
    }

    private getToothLayoutElement(): HTMLElement | null {
        return this.toothLayout?.nativeElement || null;
    }

    get mapShellWidth(): number {
        return Math.ceil(this.baseMapWidth * this.mapScale);
    }

    get mapShellHeight(): number {
        return Math.ceil(this.baseMapHeight * this.mapScale);
    }

    getMapResponsiveTransform(): string {
        return `scale(${this.mapScale})`;
    }

    private initializeResponsiveMap() {
        setTimeout(() => {
            const layout = this.getToothLayoutElement();
            if (!layout) return;
            const baseScale = this.getBaseScaleRatios();
            layout.style.setProperty('--tooth-base-scale-x', `${baseScale.x}`);
            layout.style.setProperty('--tooth-base-scale-y', `${baseScale.y}`);
            layout.style.setProperty('--tooth-base-scale-x-inverse', `${1 / baseScale.x}`);
            layout.style.setProperty('--tooth-base-scale-y-inverse', `${1 / baseScale.y}`);
            this.baseMapWidth = layout.offsetWidth * baseScale.x;
            this.baseMapHeight = layout.offsetHeight * baseScale.y;
            this.updateMapScale();

            const ResizeObserverCtor = (window as any).ResizeObserver;
            if (ResizeObserverCtor && this.mapViewport?.nativeElement) {
                this.mapResizeObserver = new ResizeObserverCtor(() => this.updateMapScale());
                this.mapResizeObserver.observe(this.mapViewport.nativeElement);
            } else {
                window.addEventListener('resize', this.updateMapScale);
            }
        }, 0);
    }

    private updateMapScale = () => {
        const viewport = this.mapViewport?.nativeElement;
        if (!viewport || !this.baseMapWidth || !this.baseMapHeight) return;
        const availableWidth = Math.max(0, viewport.clientWidth - 8);
        const availableHeight = Math.max(0, viewport.clientHeight - 8);
        const fitScale = Math.min(availableWidth / this.baseMapWidth, availableHeight / this.baseMapHeight);
        this.mapScale = Math.max(1, fitScale || 1);
    };

    private maxSeverity(current: RuleSeverity | null, next: RuleSeverity): RuleSeverity {
        if (!current) return next;
        return this.severityOrder[current] <= this.severityOrder[next] ? current : next;
    }

    private setMarkerColor(elementId: string, severity: RuleSeverity) {
        this.setElementFill(elementId, this.severityColors[severity]);
        this.setFaceOutlineAttribute(elementId, 'data-marker-severity', severity);
    }

    private setElementFill(elementId: string, color: string) {
        const element = this.getToothLayoutElement()?.querySelector<SVGElement>(`[id="${elementId}"]`);
        element?.setAttribute('fill', color);
        element?.querySelectorAll<SVGElement>('[fill]').forEach(child => child.setAttribute('fill', color));
    }

    private setElementAttribute(elementId: string, attribute: string, value: string) {
        this.getToothLayoutElement()?.querySelector<SVGElement>(`[id="${elementId}"]`)?.setAttribute(attribute, value);
    }

    private setFaceOutlineAttribute(elementId: string, attribute: string, value: string) {
        const element = this.getToothLayoutElement()?.querySelector<SVGElement>(`[id="${elementId}"]`);
        element?.setAttribute(attribute, value);
        const face = elementId.charAt(elementId.length - 1);
        element?.closest('svg')?.querySelector<SVGElement>(`[id="${face}Border"]`)?.setAttribute(attribute, value);
    }

    private removeFaceOutlineAttribute(elementId: string, attribute: string) {
        const element = this.getToothLayoutElement()?.querySelector<SVGElement>(`[id="${elementId}"]`);
        element?.removeAttribute(attribute);
        const face = elementId.charAt(elementId.length - 1);
        element?.closest('svg')?.querySelector<SVGElement>(`[id="${face}Border"]`)?.removeAttribute(attribute);
    }

    private getBaseScaleRatios(): { x: number; y: number } {
        const screenWidth = window.screen.width;
        const devicePixelRatio = window.devicePixelRatio;
        return screenWidth < 1550 && devicePixelRatio > 1.0
            ? { x: 0.70, y: 0.40 }
            : { x: 0.80, y: 0.55 };
    }

    /** 既有牙圖尺寸視為 100%，外層再依窗格等比放大。 */
    getScaleRatio(): string {
        const scale = this.getBaseScaleRatios();
        return `scale(${scale.x}, ${scale.y})`;
    }

}
